import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import {
  setProfileData as setProfileDataAction,
  setBusinessData as setBusinessDataAction,
} from "../../store/providerProfileSlice";

import config from "../../config";
import {
  getServiceProviderProfileView,
  getServiceProviderBusinessDetailsView,
  uploadImage,
  updateServiceProviderProfile,
  updateServiceProviderBusiness,
} from "../../Apiservices/Auth/servers/profileServer";

import "./profile.css";

const ManageProfileTabContent = () => {
  const { id } = useParams();
  const toast = useRef(null);
  const [profileData, setProfileData] = useState(null);
  const [editableProfileData, setEditableProfileData] = useState(null);
  const [updatedFields, setUpdatedFields] = useState({});
  const [editableBusinessData, setEditableBusinessData] = useState(null);
  const [updatedBusinessFields, setUpdatedBusinessFields] = useState({});
  const [isAnyChanges, setIsAnyChanges] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();

  const { profile: reduxProfileData, business: reduxBusinessData } =
    useSelector((state) => state.providerProfile);

  const isServiceDescriptionEnabled = false;

  const fetchProviderDetails = async () => {
    try {
      const profileRes = await getServiceProviderProfileView(id);
      const businessRes = await getServiceProviderBusinessDetailsView(id);
      setProfileData(profileRes);
      setBusinessData(businessRes);
    } catch (error) {
      console.error("Failed to fetch provider data:", error);
    }
  };
  const pickFile = () => {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (event) => {
        const file = event.target.files[0];
        if (file) resolve(file);
        else reject(new Error("No file selected"));
      };
      input.click();
    });
  };
  const handleUploadUpiImage = async () => {
    try {
      const file = await pickFile();
      const id = await uploadImage(file);
      setEditableBusinessData((prev) => ({
        ...prev,
        upi_image_id: id,
      }));
      setIsAnyChanges(true);
    } catch (error) {
      console.error("Failed to upload UPI image:", error);
    }
  };

  const handleUploadGalleryImage = async () => {
    try {
      const file = await pickFile();
      const id = await uploadImage(file);
      setEditableBusinessData((prev) => ({
        ...prev,
        gallery_image_ids: [...prev.gallery_image_ids, id],
      }));
      setIsAnyChanges(true);
    } catch (error) {
      console.error("Failed to upload Gallery image:", error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProviderDetails();
    } else {
      setProfileData(reduxProfileData);
      setBusinessData(reduxBusinessData);
    }
  }, [id, reduxProfileData, reduxBusinessData]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.current?.show({
      severity: "success",
      summary: "Copied",
      detail: "UPI ID copied to clipboard",
      life: 2000,
    });
  };
  const handleEditClick = () => {
    setIsEditing(true);
    setEditableProfileData({
      firstName: profileData.firstName || "",
      lastName: profileData.lastName || "",
      mobileNumber: profileData.mobileNumber || "",
      emailId: profileData.emailId || "",
    });
    setEditableBusinessData({
      business_name: businessData.business_name || "",
      short_description: businessData.short_description || "",
      long_description: businessData.long_description || "",
      upi_id: businessData.upi_id || "",
      upi_image_id: businessData.upi_image_id || null, // ✅ add
      gallery_image_ids: businessData.galleryImages?.map((img) => img.id) || [], // ✅ add
    });
    setUpdatedFields({});
    setUpdatedBusinessFields({});
    setIsAnyChanges(false);
  };

  const checkIfAnyChanges = (updatedProfile, updatedBusiness) => {
    setIsAnyChanges(
      (updatedProfile && Object.keys(updatedProfile).length > 0) ||
        (updatedBusiness && Object.keys(updatedBusiness).length > 0)
    );
  };
  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;

    setEditableProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setUpdatedFields((prev) => {
      const updated = { ...prev };

      if (value !== profileData[name]) {
        updated[name] = value;
      } else {
        delete updated[name];
      }

      checkIfAnyChanges(updated, updatedBusinessFields);
      return updated;
    });
  };
  const handleBusinessInputChange = (e) => {
    const { name, value } = e.target;

    const originalValue = businessData?.[name] || "";

    setEditableBusinessData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setUpdatedBusinessFields((prev) => {
      const updated = { ...prev };

      if (value !== originalValue) {
        updated[name] = value;
      } else {
        delete updated[name];
      }

      checkIfAnyChanges(updatedFields, updated);
      return updated;
    });
  };

  const handleSaveChanges = async () => {
    if (!isAnyChanges) {
      toast.current?.show({
        severity: "info",
        summary: "No Changes",
        detail: "No fields were changed.",
        life: 2000,
      });
      setIsEditing(false);
      return;
    }

    try {
      console.log("Final Updated Profile Fields:", updatedFields);

      const finalBusinessUpdate = {
        ...updatedBusinessFields,
        upi_image_id: editableBusinessData.upi_image_id || null,
        gallery_image_ids: editableBusinessData.gallery_image_ids || [],
      };

      console.log("Final Updated Business Fields:", finalBusinessUpdate);

      // 🚀 Only call APIs if there are changes
      if (Object.keys(updatedFields).length > 0) {
        await updateServiceProviderProfile(id, updatedFields);
      }

      const isBusinessUpdateNeeded =
        Object.keys(updatedBusinessFields).length > 0 ||
        editableBusinessData.upi_image_id !== businessData.upi_image_id ||
        JSON.stringify(editableBusinessData.gallery_image_ids) !==
          JSON.stringify(
            businessData.galleryImages?.map((img) => img.id) || []
          );

      if (isBusinessUpdateNeeded) {
        await updateServiceProviderBusiness(id, finalBusinessUpdate);
      }

      if (id) {
        fetchProviderDetails();
      } else {
        if (Object.keys(updatedFields).length > 0) {
          dispatch(setProfileDataAction({ ...profileData, ...updatedFields }));
        }

        if (isBusinessUpdateNeeded) {
          dispatch(
            setBusinessDataAction({
              ...businessData,
              ...finalBusinessUpdate,
            })
          );
        }
      }

      setIsEditing(false);
      setUpdatedFields({});
      setUpdatedBusinessFields({});
      setIsAnyChanges(false);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Profile and Business info updated successfully",
        life: 2000,
      });
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update profile/business info",
        life: 3000,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditableProfileData(null);
    setEditableBusinessData(null);
    setUpdatedFields({});
    setUpdatedBusinessFields({});
    setIsAnyChanges(false);
  };

  if (!profileData || !businessData) return <p>Loading...</p>;

  const hasUPI = businessData?.upi_id || businessData?.upi_image_id;
  const hasGalleryImages = businessData?.galleryImages?.length > 0;

  const initials = profileData.firstName?.[0]?.toUpperCase() || "?";

  return (
    <div className="profile-wrapper">
      <div className="profile-header-actions">
        {!id &&
          (!isEditing ? (
            <Button
              label="Edit"
              icon="pi pi-pencil"
              onClick={handleEditClick}
              className="p-button-sm p-button-outlined edit-action-btn"
            />
          ) : (
            <div className="edit-action-buttons">
              <Button
                label="Save Changes"
                icon="pi pi-save"
                onClick={handleSaveChanges}
                className="p-button-sm p-button-success edit-action-btn"
                disabled={!isAnyChanges}
              />
              <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={handleCancelEdit}
                className="p-button-sm p-button-secondary edit-action-btn"
              />
            </div>
          ))}
      </div>
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          {profileData.profilePicUrl ? (
            <img
              src={profileData.profilePicUrl}
              alt="Profile"
              onError={(e) => (e.target.style.display = "none")}
            />
          ) : (
            <Avatar
              label={initials}
              size="xlarge"
              shape="circle"
              style={{ backgroundColor: "#3f51b5" }}
            />
          )}
        </div>
        <div className={`profile-info`}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h2>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="firstName"
                    value={editableProfileData.firstName}
                    onChange={handleProfileInputChange}
                    placeholder="First Name"
                    className="edit-input"
                  />{" "}
                  <input
                    type="text"
                    name="lastName"
                    value={editableProfileData.lastName}
                    onChange={handleProfileInputChange}
                    placeholder="Last Name"
                    className="edit-input"
                  />
                </>
              ) : (
                `${profileData.firstName} ${profileData.lastName}`
              )}
            </h2>
          </div>
          <p>
            <strong>Phone:</strong>{" "}
            {isEditing ? (
              <input
                type="text"
                name="mobileNumber"
                value={editableProfileData.mobileNumber}
                onChange={handleProfileInputChange}
                placeholder="Mobile Number"
                className="edit-input"
              />
            ) : (
              profileData.mobileNumber
            )}
          </p>
          <p>
            <strong>Email:</strong>{" "}
            {isEditing ? (
              <input
                type="email"
                name="emailId"
                value={editableProfileData.emailId}
                onChange={handleProfileInputChange}
                placeholder="Email"
                className="edit-input"
              />
            ) : (
              `${profileData.emailId} [${
                profileData.isEmailIdVerified ? "Verified" : "Unverified"
              }]`
            )}
          </p>
          <p>
            <strong>Address:</strong>{" "}
            {profileData.addresses?.[0]?.formattedAddress}
          </p>
        </div>
      </div>

      {/* Business Information */}
      <div className="profile-section">
        <h3>Business Information </h3>
        <p>
          <strong>Name:</strong>{" "}
          {isEditing ? (
            <input
              type="text"
              name="business_name"
              value={editableBusinessData.business_name}
              onChange={handleBusinessInputChange}
              className="edit-input"
              placeholder="Business Name"
            />
          ) : (
            businessData.business_name
          )}
        </p>

        <p>
          <strong>Short Description:</strong>{" "}
          {isEditing ? (
            <input
              type="text"
              name="short_description"
              value={editableBusinessData.short_description}
              onChange={handleBusinessInputChange}
              className="edit-input"
              placeholder="Short Description"
            />
          ) : (
            businessData.short_description
          )}
        </p>
        <p>
          {isEditing ? (
            <textarea
              name="long_description"
              value={editableBusinessData.long_description}
              onChange={handleBusinessInputChange}
              className="edit-input"
              placeholder="Long Description"
              rows="4"
            />
          ) : (
            businessData.long_description
          )}
        </p>

        {/* UPI Section */}
        {isEditing || hasUPI ? (
          <div className="upi-section">
            {isEditing ? (
              <>
                {/* Editable UPI Image */}
                {editableBusinessData.upi_image_id ? (
                  <div className="image-item">
                    <img
                      src={`${config.API_BASE_URL}/gallery/download/${editableBusinessData.upi_image_id}`}
                      alt="UPI QR"
                      className="upi-image"
                    />
                    <Button
                      icon="pi pi-times"
                      className="p-button-danger p-button-sm"
                      onClick={() => {
                        setEditableBusinessData((prev) => ({
                          ...prev,
                          upi_image_id: null,
                        }));
                        setIsAnyChanges(true);
                      }}
                    />
                  </div>
                ) : (
                  <Button
                    label="Add UPI Image"
                    icon="pi pi-plus"
                    className="p-button-sm"
                    onClick={handleUploadUpiImage}
                  />
                )}

                {/* Editable UPI ID */}
                <div className="upi-id-wrap" style={{ marginTop: "1rem" }}>
                  <input
                    type="text"
                    name="upi_id"
                    value={editableBusinessData.upi_id}
                    onChange={handleBusinessInputChange}
                    className="edit-input"
                    placeholder="UPI ID"
                  />
                  <Button
                    icon="pi pi-copy"
                    className="p-button-sm p-button-text"
                    onClick={() => copyToClipboard(editableBusinessData.upi_id)}
                  />
                </div>
              </>
            ) : (
              <>
                {businessData.upi_image_id && (
                  <>
                    <Tooltip target=".upi-image" content="Click to enlarge" />
                    <img
                      src={`${config.API_BASE_URL}/gallery/download/${businessData.upi_image_id}`}
                      alt="UPI QR"
                      className="upi-image"
                      onClick={() => setVisible(true)}
                    />
                    <Dialog
                      header="Scan UPI"
                      visible={visible}
                      style={{ width: "320px" }}
                      onHide={() => setVisible(false)}
                      footer={
                        <Button
                          label="OK"
                          onClick={() => setVisible(false)}
                          autoFocus
                        />
                      }
                      dismissableMask
                    >
                      <div style={{ textAlign: "center" }}>
                        <img
                          src={`${config.API_BASE_URL}/gallery/download/${businessData.upi_image_id}`}
                          alt="UPI Full"
                          className="upi-full-image"
                        />
                        {businessData.upi_id && (
                          <div
                            style={{
                              marginTop: "1rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.5rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <strong>{businessData.upi_id}</strong>
                            <Button
                              icon="pi pi-copy"
                              className="p-button-sm p-button-secondary"
                              onClick={() =>
                                copyToClipboard(businessData.upi_id)
                              }
                            />
                          </div>
                        )}
                      </div>
                    </Dialog>
                  </>
                )}
                {businessData.upi_id && (
                  <div className="upi-id-wrap">
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          name="upi_id"
                          value={editableBusinessData.upi_id}
                          onChange={handleBusinessInputChange}
                          className="edit-input"
                          placeholder="UPI ID"
                        />
                        <Button
                          icon="pi pi-copy"
                          className="p-button-sm p-button-text"
                          onClick={() =>
                            copyToClipboard(editableBusinessData.upi_id)
                          }
                        />
                      </>
                    ) : (
                      <>
                        <span>{businessData.upi_id}</span>
                        <Button
                          icon="pi pi-copy"
                          className="p-button-sm p-button-text"
                          onClick={() => copyToClipboard(businessData.upi_id)}
                        />
                      </>
                    )}
                  </div>
                )}
                <Toast ref={toast} />
              </>
            )}
          </div>
        ) : (
          <Message severity="warn" text="UPI information is not available." />
        )}
      </div>

      {/* Gallery Section */}
      <div className="profile-section">
        <h3>Gallery Images</h3>
        {isEditing || hasGalleryImages ? (
          <div className="gallery-grid">
            {(isEditing
              ? editableBusinessData.gallery_image_ids
              : businessData.galleryImages?.map((img) => img.id)
            ).map((id, idx) => (
              <div key={idx} className="image-item">
                <img
                  src={`${config.API_BASE_URL}/gallery/download/${id}`}
                  alt={`Gallery ${idx + 1}`}
                  className="gallery-img"
                />
                {isEditing && (
                  <Button
                    icon="pi pi-times"
                    className="p-button-danger p-button-sm"
                    onClick={() => {
                      setEditableBusinessData((prev) => ({
                        ...prev,
                        gallery_image_ids: prev.gallery_image_ids.filter(
                          (imgId) => imgId !== id
                        ),
                      }));
                      setIsAnyChanges(true);
                    }}
                  />
                )}
              </div>
            ))}

            {/* Add Gallery Button */}
            {isEditing && editableBusinessData.gallery_image_ids.length < 4 && (
              <Button
                label="Add Image"
                icon="pi pi-plus"
                className="p-button-sm"
                onClick={handleUploadGalleryImage} // 🔥 make sure this function exists
              />
            )}
          </div>
        ) : (
          <Message severity="warn" text="Gallery images are not available." />
        )}
      </div>

      {/* Services Section */}
      <div className="profile-section">
        <h3>My Services</h3>
        {profileData.services.length === 0 ? (
          <Message severity="info" text="No services found." />
        ) : (
          <div className="services-chip-wrap">
            {profileData.services.map((service) => (
              <div key={service.id} className="service-chip">
                {service.serviceName}
                {isServiceDescriptionEnabled && (
                  <div className="service-description">
                    My Dummy Description
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageProfileTabContent;
